import set from "lodash-es/set.js";
import config from "./config";

const toTranslationObject = (result: any): Record<string, any> => {
  return result.data.reduce((acc: any, item: any) => {
    return set({ ...acc }, item.key, item.translation);
  }, {});
};

export type StrapiConfig = {
  baseUrl?: string;
  apiToken?: string;
  limit?: number;
};

const createStrapi = ({ baseUrl, apiToken, limit }: StrapiConfig = {}) => {
  const strapiBaseUrl =
    baseUrl || config.strapi.baseUrl || "http://localhost:1337";
  const token = apiToken || config.strapi.apiToken;
  const maxRetries = 3;
  const retryDelay = 2000; //milliseconds

  const chunkSize = limit || 1000;

  const buildStrapiUrl = (params: Record<string, any>): string => {
    const url = new URL(`${strapiBaseUrl}/api/translations`);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((v) => url.searchParams.append(key, v.toString()));
        } else {
          url.searchParams.set(key, value.toString());
        }
      }
    });

    return url.toString();
  };

  const fetchFromStrapi = async (url: string) => {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let lastError: Error & { statusCode?: number };

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, { headers });

        if (response.ok) {
          return response.json();
        }

        const shouldRetry =
          response.status === 429 ||
          response.status >= 500 ||
          response.status === 408 ||
          response.status === 503 ||
          response.status === 504;

        const error = new Error(
          `Strapi API error: ${response.status} ${response.statusText}`
        ) as Error & { statusCode: number };
        error.statusCode = response.status;

        if (!shouldRetry) {
          throw error;
        }

        lastError = error;

        const retryAfter = response.headers.get("Retry-After");
        let waitTime = retryDelay * Math.pow(2, attempt);

        if (retryAfter) {
          const retryAfterSeconds = parseInt(retryAfter);
          if (!isNaN(retryAfterSeconds)) {
            waitTime = retryAfterSeconds * 1000;
          }
        }

        if (attempt < maxRetries - 1) {
          console.warn(
            `Retrying ${url} after ${waitTime}ms (attempt ${
              attempt + 1
            }/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      } catch (error) {
        lastError = error as Error & { statusCode?: number };

        if (lastError.statusCode) {
          const status = lastError.statusCode;
          if (
            status >= 400 &&
            status < 500 &&
            status !== 429 &&
            status !== 408
          ) {
            throw error;
          }
        }

        if (attempt < maxRetries - 1) {
          const waitTime = retryDelay * Math.pow(2, attempt);
          console.warn(
            `Network error, retrying ${url} after ${waitTime}ms (attempt ${
              attempt + 1
            }/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }
    }

    throw new Error(
      `Failed after ${maxRetries} attempts: ${lastError!.message}`
    );
  };

  const getTranslation = async ({
    applicationSlug,
    locale,
    start = 0,
    items = [],
    limit = chunkSize,
  }: {
    applicationSlug: string | string[];
    locale: string;
    start?: number;
    items?: any[];
    limit?: number;
  }): Promise<any> => {
    const slugs = Array.isArray(applicationSlug)
      ? applicationSlug
      : [applicationSlug];
    const params = {
      locale: locale,
      "pagination[start]": start,
      "pagination[limit]": limit,
      populate: "applications",
      "filters[applications][slug][$in]": [`common`, ...slugs],
    };

    const url = buildStrapiUrl(params);

    try {
      const response = await fetchFromStrapi(url);
      const { data: chunkItems, meta } = response;

      const nextStart = start + chunkSize;
      const allItems = [...items, ...chunkItems];

      // Check if there are more items to fetch
      if (meta.pagination && meta.pagination.total > nextStart) {
        return getTranslation({
          applicationSlug,
          locale,
          items: allItems,
          start: nextStart,
          limit,
        });
      }

      return toTranslationObject({ data: allItems });
    } catch (error) {
      console.error("Error fetching translations from Strapi:", error);
      throw error;
    }
  };

  return { getTranslation };
};

export default createStrapi;
